import { useCallback } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import type { ChatbotSessionRequest, ChatbotSessionResponse } from '@/models/chatbot';
import { getChatbotApiBaseUrl, isRecord, readChatbotErrorMessage, sendChatbotRequest } from './chatbotApiRequest';
import { fetchWithAuthRetry } from './withAuthRetry';

class SessionError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'SessionError';
    this.status = status;
  }
}

function isSessionError(value: unknown): value is SessionError {
  return value instanceof SessionError;
}

function isValidSessionResponse(data: unknown): data is ChatbotSessionResponse {
  if (!isRecord(data)) return false;
  if (!('session_id' in data) || !('stream_url' in data)) return false;
  if (typeof data.session_id !== 'string' || typeof data.stream_url !== 'string') return false;
  return !('conversation_id' in data) || typeof data.conversation_id === 'string';
}

interface UseChatbotSessionReturn {
  createSession: (request: ChatbotSessionRequest) => Promise<ChatbotSessionResponse>;
}

const useChatbotSession = (): UseChatbotSessionReturn => {
  const { token, ensureValidSession } = useAuthContext();

  const createSession = useCallback(
    async (request: ChatbotSessionRequest): Promise<ChatbotSessionResponse> => {
      const baseUrl = getChatbotApiBaseUrl();
      if (!baseUrl) {
        throw new SessionError(0, 'Chatbot API URL is not configured');
      }

      const url = `${baseUrl}/chatbot/sessions`;
      const response = await fetchWithAuthRetry(
        authToken => sendChatbotRequest(url, { method: 'POST', body: request }, authToken),
        { token, ensureValidSession },
        (status, message) => new SessionError(status, message),
      );

      if (!response.ok) {
        const errorMessage = await readChatbotErrorMessage(response);
        throw new SessionError(response.status, errorMessage);
      }

      const data: unknown = await response.json();
      if (!isValidSessionResponse(data)) {
        throw new SessionError(0, 'Invalid session response from server');
      }

      return data;
    },
    [ensureValidSession, token],
  );

  return { createSession };
};

export { isSessionError, SessionError };
export default useChatbotSession;
